import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Table, Checkbox, Icon, Message, Popup } from "semantic-ui-react";
import i18nstring, { strings } from "../../i18n";
import { Colors } from "../../constants/Styles";
import { Button } from "../atoms/Button";
import ConfirmationPopup from "../atoms/ConfirmationPopup";
import { AddUserModal } from "../organisms/Modal";
import {
  getEmailPreferencesResponses,
  handleDeleteEmailPreferences,
  handleSaveEmailPreferences,
  handleUpdateEmailPreferences
} from "../../redux/actions/EmailPreferences";
import {
  NotificationPreferenceType,
  NotificationPreferenceTypeList
} from "../../Shared/Constants/Notification";

const tableStyle = {
  fontFamily: "Lato, sans-serif",
  borderCollapse: "collapse"
};

function EmailNotifications({
  user,
  emailsPrefs,
  attemptGetEmailPreferences,
  attemptSaveEmailPreferences,
  attemptUpdateEmailPreferences,
  attemptDeleteEmailPreferences
}) {
  const notificationTypes = useMemo(() => NotificationPreferenceTypeList, []);
  const [confirmationType, setConfirmationType] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tempEmailPrefs, setTempEmailPrefs] = useState(null);
  const [emailPreferencesList, setEmailPreferencesList] = useState([]);

  const NotificationLabels = useMemo(
    () => ({
      pickupScheduled: i18nstring(strings.PICKUP_SCHEDULED_NOTIFICATION),
      dropoffScheduled: i18nstring(strings.DROPOFF_SCHEDULED_NOTIFICATION),
      newMessage: i18nstring(strings.NEW_CHAT_NOTIFICATION),
      newCourier: i18nstring(strings.NEW_COURIER_NOTIFICATION),
      donationReport: i18nstring(strings.DONATION_REPORT_NOTIFICATION),
      donationEdited: i18nstring(strings.DONATION_EDITED_NOTIFICATION)
    }),
    []
  );

  const NotificationTooltips = useMemo(
    () => ({
      pickupScheduled: i18nstring(strings.PICKUP_REQUESTED_NOTIFICATION),
      dropoffScheduled: i18nstring(
        strings.DROPOFF_SCHEDULED_NOTIFICATION_DETAIL
      ),
      newMessage: i18nstring(strings.NEW_CHAT_MESSAGE_NOTIFICATION),
      newCourier: i18nstring(strings.NEW_COURIER_NOTIFICATION_DETAIL),
      donationReport: i18nstring(strings.DAILY_DONATION_REPORT_NOTIFICATION),
      donationEdited: i18nstring(strings.DONATION_EDITED_ALERT)
    }),
    []
  );

  useEffect(() => {
    attemptGetEmailPreferences();
  }, []);

  const getDefaultNotificationPrefs = () =>
    Object.fromEntries(Object.values(NotificationPreferenceType).map(type => [type, true]));

  useEffect(() => {
    if (!emailsPrefs || !user?.email) return;
    const currentUserItem = emailsPrefs.find(item => item.email === user.email);
    const list = currentUserItem
      ? [currentUserItem, ...emailsPrefs.filter(item => item.email !== user.email)]
      : [{ id: "default", email: user.email, notifications: getDefaultNotificationPrefs() }, ...emailsPrefs];
    setEmailPreferencesList(list);
  }, [emailsPrefs, user?.email]);

  const toggleCheckbox = (email, type) => {
    setTempEmailPrefs(prev =>
      prev.map(item =>
        item.email !== email
          ? item
          : { ...item, notifications: { ...item.notifications, [type]: !item.notifications[type] } }
      )
    );
  };

  const startEditing = () => {
    setTempEmailPrefs(emailPreferencesList.map(user => ({
      ...user,
      notifications: { ...user.notifications }
    })));
    setIsEditing(true);
  };

  const saveChanges = () => {
    if (tempEmailPrefs) {
      attemptUpdateEmailPreferences(tempEmailPrefs);
      setEmailPreferencesList(tempEmailPrefs);
    }
    setTempEmailPrefs(null);
    setIsEditing(false);
  };

  const cancelEditing = () => {
    setTempEmailPrefs(null);
    setIsEditing(false);
  };

  const deleteRow = email => {
    if (!email) return;
    attemptDeleteEmailPreferences(email.id);
    setEmailPreferencesList(prev => prev.filter(user => user.email !== email));
  };

  const addEmail = email => {
    if (!email) return;
    setIsEditing(false);
    const reducedNotificationTypes = notificationTypes.reduce(
        (acc, type) => ({ ...acc, [type]: true }),
        {}
      );
    const newUser = {
      email,
      notifications: reducedNotificationTypes
    };
    setEmailPreferencesList(prev => [...prev, newUser]);
    setShowAddUserModal(false);
    attemptSaveEmailPreferences(newUser);
       // If there's no item in emailsPrefs with id === "default", create & save one
    if (user?.email && !emailsPrefs.some(item => item.id === "default")) {
      const defaultItem = {
        id: "default",
        email: user.email,
        notifications: reducedNotificationTypes
      };
      attemptSaveEmailPreferences(defaultItem);
    }
  };

  const handleConfirmation = type => {
    if (type === "delete") deleteRow(selectedEmail);
    if (type === "save") saveChanges();
    if (type === "cancel") cancelEditing();
    setConfirmationType(null);
  };

  const EmailRow = ({ item, index }) => (
    <Table.Row key={item.email}>
      <Table.Cell className="email-cell">
        <p style={{ color: "#333", fontWeight: index === 0 ? 700 : 500 }}>
          {item.email}
        </p>
      </Table.Cell>
      {notificationTypes.map(type => (
        <Table.Cell key={type} textAlign="center">
          <Checkbox
            toggle
            className="custom-toggle"
            disabled={!isEditing}
            checked={Boolean(item.notifications?.[type])}
            onChange={() => toggleCheckbox(item.email, type)}
          />
        </Table.Cell>
      ))}
      {index > 0 && (
        <Table.Cell textAlign="center">
          <Icon
            name="trash alternate"
            color="orange"
            className="info-icon"
            size="large"
            onClick={() => {
              setSelectedEmail(item);
              setConfirmationType("delete");
            }}
          />
        </Table.Cell>
      )}
    </Table.Row>
  );

  const getCurrentList = () =>
    isEditing ? tempEmailPrefs : emailPreferencesList;

  return (
    <div className="main_container_section">
      <div className="refresh_container">
        {isEditing ? (
          <Popup
            content={i18nstring(strings.ADD_USER_DISABLED_TOOLTIP)}
            on="hover"
            hoverable
            position="top left"
            className="popup"
            trigger={
              <span>
                <Button
                  onClick={() => setShowAddUserModal(true)}
                  disabled={isEditing}
                >
                  {i18nstring(strings.ADD_USER)}
                </Button>
              </span>
            }
          />
        ) : (
          <Button
            onClick={() => setShowAddUserModal(true)}
            disabled={isEditing}
          >
            {i18nstring(strings.ADD_USER)}
          </Button>
        )}
      </div>

      <div className="main_container_table">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <div className="table_title" style={{ marginTop: 12 }}>
            {i18nstring(strings.NOTIFICATION_SETTINGS)}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 10,
              marginRight: 12,
              marginTop: 10
            }}
          >
            {!isEditing ? (
              <Button basic onClick={startEditing}>
                {i18nstring(strings.EDIT)}
              </Button>
            ) : (
              <>
                <Button
                  basic
                  color={Colors.lightGrey}
                  onClick={() => setConfirmationType("cancel")}
                >
                  {i18nstring(strings.CANCEL)}
                </Button>
                <Button basic onClick={() => setConfirmationType("save")}>
                  {i18nstring(strings.SAVE)}
                </Button>
              </>
            )}
          </div>
        </div>
        <span className="email-info-text">
          {i18nstring(strings.EMAIL_PREFS_INFO)}
        </span>
        <Table celled fixed style={tableStyle}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className="header-cell"
                style={{ width: 235, backgroundColor: Colors.lightGreyBg }}
              >
                {i18nstring(strings.EMAIL)}
              </Table.HeaderCell>
              {notificationTypes.map(type => (
                <Table.HeaderCell
                  textAlign="center"
                  key={type}
                  className="header-cell"
                >
                  <div className="header-label">
                    <span>{NotificationLabels[type] || type}</span>

                    <Popup
                      content={NotificationTooltips[type]}
                      on="hover"
                      hoverable
                      position="top center"
                      className="popup"
                      trigger={
                        <Icon
                          name="info circle"
                          className="info-icon"
                          size="small"
                        />
                      }
                    />
                  </div>
                </Table.HeaderCell>
              ))}
              <Table.HeaderCell
                style={{ width: 70, backgroundColor: Colors.lightGreyBg }}
              />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {getCurrentList()?.map((item, index) => (
              <EmailRow item={item} index={index} key={item.email} />
            ))}
          </Table.Body>
        </Table>
      </div>
      {confirmationType && (
        <ConfirmationPopup
          title={i18nstring(strings.CONFIRMATION)}
          message={
            confirmationType === "delete"
              ? `${i18nstring(strings.DELETE_USER_CONFIRMATION_MESSAGE)} ${
                  selectedEmail.email
                }?`
              : confirmationType === "save"
              ? i18nstring(strings.CONFIRM_SAVE_CHANGES)
              : i18nstring(strings.CONFIRM_CANCEL_CHANGES)
          }
          primaryButtonText={i18nstring(strings.CONFIRM)}
          onPrimaryButtonClicked={() => handleConfirmation(confirmationType)}
          open={!!confirmationType}
          setOpen={() => setConfirmationType(null)}
        />
      )}
      {showAddUserModal && (
        <AddUserModal
          existingEmails={emailsPrefs.map(item => item.email)}
          saveEmail={(userId, email) => addEmail(email)}
          toggle={showAddUserModal}
          setToggle={setShowAddUserModal}
        />
      )}
    </div>
  );
}

const mapStateToProps = state => ({
  user: state.account.user,
  emailsPrefs: state.emailsPrefs.items
});

const mapDispatchToProps = dispatch => ({
  attemptGetEmailPreferences: () => dispatch(getEmailPreferencesResponses()),
  attemptSaveEmailPreferences: data => dispatch(handleSaveEmailPreferences(data)),
  attemptUpdateEmailPreferences: data => dispatch(handleUpdateEmailPreferences(data)),
  attemptDeleteEmailPreferences: data => dispatch(handleDeleteEmailPreferences(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(EmailNotifications);
